import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import type { DocumentContent, DocumentContentLine, PaperFigure } from "@/features/clipboard/api/clipboard";
import {
  getDocumentFigureBlob,
  getDocumentHtml,
  getPaperFigures,
} from "@/features/clipboard/api/clipboard";
import { HtmlRenderer, PREVIEW_STYLES } from "@/features/clipboard/components/HtmlRenderer";
import { RawFileRenderer } from "@/features/clipboard/components/RawFileRenderer";
import {
  detectFileType,
  isBrowserImage,
  type FileType,
} from "@/features/clipboard/utils/fileTypes";
import { cn } from "@/utils/cn";
import { escapeHtml, KATEX_IFRAME_HEAD, renderTextWithMathToHtml } from "@/utils/math";
import { sanitizeImageBlobForPreview } from "@/utils/sanitize";
import { Maximize2, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";

function linesToString(lines: DocumentContentLine[]): string {
  return lines.map((l) => l.content).join("\n");
}

function JsonRenderer({ content }: { content: string }) {
  const formatted = useMemo(() => {
    try {
      return JSON.stringify(JSON.parse(content), null, 2);
    } catch {
      return content;
    }
  }, [content]);

  return (
    <pre className="font-mono text-xs leading-relaxed text-zinc-700 whitespace-pre-wrap break-words">
      {formatted}
    </pre>
  );
}

function StyledDocRenderer({ lines, truncated }: { lines: DocumentContentLine[]; truncated?: boolean }) {
  const html = useMemo(() => {
    let currentSection = "";
    const parts: string[] = [];
    for (const line of lines) {
      const section = line.section || "";
      if (section && section !== currentSection) {
        currentSection = section;
        parts.push(`<h2>${escapeHtml(section)}</h2>`);
      }
      // Render $…$/$$…$$ (and \(…\)/\[…\]) as KaTeX; HTML-escape the rest.
      parts.push(`<p>${renderTextWithMathToHtml(line.content)}</p>`);
    }
    if (truncated) {
      parts.push(`<p style="color:#999;font-style:italic">… truncated (${lines.length} lines shown)</p>`);
    }
    return parts.join("\n");
  }, [lines, truncated]);

  return (
    <iframe
      srcDoc={KATEX_IFRAME_HEAD + PREVIEW_STYLES + html}
      sandbox="allow-popups"
      className="w-full border-0 bg-white"
      style={{ height: "calc(100vh - 8rem)", minHeight: "500px" }}
      title="Document preview"
      referrerPolicy="no-referrer"
    />
  );
}

// Inline-math / equation images (e.g. PMC "..._Article_Equ1.gif", "IEq3"),
// which duplicate the KaTeX-rendered $…$ math and just add noise to a gallery.
const EQUATION_IMG_RE = /(?:^|[_-])i?equ?\d/i;

function figureImgHtml(fig: PaperFigure, origin: string, label: string): string {
  const src = fig.url.startsWith("http") ? fig.url : `${origin}${fig.url}`;
  const caption = label
    ? `<figcaption>${label
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")}</figcaption>`
    : "";
  return `<figure><img loading="lazy" src="${src}" alt="${label || fig.filename}">${caption}</figure>`;
}

/**
 * Rich preview for corpus-linked docs (PMC / arXiv / bioRxiv / medRxiv):
 * line-numbered text with KaTeX math plus the paper's extracted figure/table
 * images. Figures are placed inline right after their caption line when a
 * position is known, and any remaining figures are shown in a trailing gallery.
 */
function CorpusDocRenderer({
  lines,
  truncated,
  corpusDocId,
}: {
  lines: DocumentContentLine[];
  truncated?: boolean;
  corpusDocId: string;
}) {
  const [figures, setFigures] = useState<PaperFigure[]>([]);

  useEffect(() => {
    let cancelled = false;
    getPaperFigures(corpusDocId).then((figs) => {
      if (!cancelled) setFigures(figs);
    });
    return () => {
      cancelled = true;
    };
  }, [corpusDocId]);

  const html = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const usable = figures.filter((f) => f.url && !EQUATION_IMG_RE.test(f.filename));

    const byLine = new Map<number, PaperFigure[]>();
    const placed = new Set<PaperFigure>();
    for (const fig of usable) {
      if (typeof fig.line_num === "number") {
        const arr = byLine.get(fig.line_num) ?? [];
        arr.push(fig);
        byLine.set(fig.line_num, arr);
        placed.add(fig);
      }
    }

    let currentSection = "";
    const parts: string[] = [];
    for (const line of lines) {
      const section = line.section || "";
      if (section && section !== currentSection) {
        currentSection = section;
        parts.push(`<h2>${escapeHtml(section)}</h2>`);
      }
      parts.push(`<p>${renderTextWithMathToHtml(line.content)}</p>`);
      const inlineFigs = byLine.get(line.line_number);
      if (inlineFigs) {
        for (const fig of inlineFigs) {
          const label = fig.figure_num
            ? `Figure ${fig.figure_num}`
            : fig.table_num
              ? `Table ${fig.table_num}`
              : "";
          parts.push(figureImgHtml(fig, origin, label));
        }
      }
    }

    if (truncated) {
      parts.push(
        `<p style="color:#999;font-style:italic">… truncated (${lines.length} lines shown)</p>`,
      );
    }

    const gallery = usable.filter((f) => !placed.has(f));
    if (gallery.length > 0) {
      parts.push(`<hr><h2>Figures</h2>`);
      for (const fig of gallery) {
        const label = fig.figure_num
          ? `Figure ${fig.figure_num}`
          : fig.table_num
            ? `Table ${fig.table_num}`
            : fig.filename;
        parts.push(figureImgHtml(fig, origin, label));
      }
    }

    return parts.join("\n");
  }, [lines, truncated, figures]);

  return (
    <iframe
      srcDoc={KATEX_IFRAME_HEAD + PREVIEW_STYLES + html}
      // allow-same-origin lets the srcdoc resolve same-origin image URLs and
      // scroll in-page; scripts stay blocked (no allow-scripts).
      sandbox="allow-popups allow-same-origin"
      className="w-full border-0 bg-white"
      style={{ height: "calc(100vh - 8rem)", minHeight: "500px" }}
      title="Document preview"
      referrerPolicy="no-referrer"
    />
  );
}

function TextRenderer({ lines, truncated }: { lines: DocumentContentLine[]; truncated?: boolean }) {
  return (
    <div className="font-mono text-xs leading-relaxed text-zinc-700 space-y-0.5">
      {lines.map((l) => (
        <div key={l.line_number} className="flex gap-3">
          <span className="shrink-0 select-none text-zinc-300 w-10 text-right">
            L{l.line_number}
          </span>
          <span className="whitespace-pre-wrap">{l.content}</span>
        </div>
      ))}
      {truncated && (
        <div className="pt-3 text-zinc-400">
          &hellip; truncated. Use <code>paperclip cat</code> for the full
          document.
        </div>
      )}
    </div>
  );
}

type PreviewView = "parsed" | "raw";

function VirtualImageRenderer({
  docId,
  filename,
  ownerId,
}: {
  docId: string;
  filename: string;
  ownerId?: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;
    setState("loading");
    setUrl(null);
    getDocumentFigureBlob(docId, filename, ownerId)
      .then(async (blob) => {
        if (cancelled) return;
        const previewBlob = await sanitizeImageBlobForPreview(blob, filename);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(previewBlob);
        setUrl(objectUrl);
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [docId, filename, ownerId]);

  if (state === "loading") {
    return <div className="text-sm text-zinc-400">Loading image&hellip;</div>;
  }
  if (state === "error" || !url) {
    return <div className="text-sm text-zinc-400">This image could not be loaded.</div>;
  }
  return (
    <div className="flex justify-center p-2">
      <img src={url} alt={filename} className="max-h-full max-w-full rounded object-contain" />
    </div>
  );
}

function PreviewContent({
  preview,
  loading,
  fileType,
  ownerId,
  view,
}: {
  preview: DocumentContent | null;
  loading: boolean;
  fileType: FileType;
  ownerId?: string;
  view: PreviewView;
}) {
  const [richFailed, setRichFailed] = useState(false);
  const docId = preview?.document?.id;
  const corpusDocId =
    typeof preview?.document?.corpus_doc_id === "string"
      ? preview.document.corpus_doc_id
      : undefined;
  const isCorpusLink =
    preview?.document?.link_type === "corpus_link" && !!corpusDocId;

  if (loading) {
    return <div className="text-sm text-zinc-400">Loading content&hellip;</div>;
  }

  if (preview?.virtual_file?.kind === "image" && docId) {
    return (
      <VirtualImageRenderer
        docId={String(docId)}
        filename={preview.virtual_file.name}
        ownerId={ownerId}
      />
    );
  }
  if (preview?.virtual_file?.kind === "meta") {
    return <JsonRenderer content={linesToString(preview.lines)} />;
  }
  if (preview?.virtual_file?.kind === "lines") {
    return (
      <StyledDocRenderer
        lines={preview.lines}
        truncated={preview.truncated}
      />
    );
  }
  if (preview?.virtual_file?.kind === "raw" && docId) {
    return <RawFileRenderer docId={String(docId)} filename={preview.virtual_file.name} />;
  }

  // Raw view: show the user's actual uploaded file. (Only offered for uploaded
  // docs — see canShowRaw in the panel.)
  if (view === "raw" && docId && preview) {
    return <RawFileRenderer docId={docId} filename={String(preview.document.filename)} />;
  }

  if (!preview || preview.lines.length === 0) {
    return (
      <div className="text-sm text-zinc-400">
        No full text for this entry &mdash; citation only. See metadata.
      </div>
    );
  }

  // Corpus links (PMC/arXiv/bioRxiv/medRxiv) never have uploaded HTML; render
  // their line-numbered text with math + extracted figure images directly.
  if (isCorpusLink && corpusDocId && fileType === "text") {
    return (
      <CorpusDocRenderer
        lines={preview.lines}
        truncated={preview.truncated}
        corpusDocId={corpusDocId}
      />
    );
  }

  const raw = linesToString(preview.lines);

  // Try rich HTML preview first (if we have a doc ID and haven't already failed)
  if (docId && !richFailed && fileType === "text") {
    return (
      <RichHtmlFallback
        docId={docId}
        ownerId={ownerId}
        onNoHtml={() => setRichFailed(true)}
        fallback={<StyledDocRenderer lines={preview.lines} truncated={preview.truncated} />}
      />
    );
  }

  if (fileType === "json") return <JsonRenderer content={raw} />;
  if (fileType === "code" || fileType === "html") {
    // HTML shows its parsed (line-addressable) source here — the same content
    // the agent/CLI reads. Rendering the original page lives in the Raw view
    // (with its Rendered/Source toggle), so agent-generated HTML isn't
    // rendered unless explicitly requested.
    return <TextRenderer lines={preview.lines} truncated={preview.truncated} />;
  }
  return <StyledDocRenderer lines={preview.lines} truncated={preview.truncated} />;
}

function RichHtmlFallback({
  docId,
  ownerId,
  onNoHtml,
  fallback,
}: {
  docId: string;
  ownerId?: string;
  onNoHtml: () => void;
  fallback: React.ReactNode;
}) {
  const [html, setHtml] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    getDocumentHtml(docId, ownerId)
      .then((result) => {
        if (!cancelled) {
          if (result?.html) {
            setHtml(result.html);
          } else {
            setHtml(null);
            onNoHtml();
          }
        }
      })
      .catch(() => {
        if (!cancelled) { setHtml(null); onNoHtml(); }
      });
    return () => { cancelled = true; };
  }, [docId, ownerId, onNoHtml]);

  if (html === undefined) return <div className="text-sm text-zinc-400">Loading preview&hellip;</div>;
  if (html === null) return <>{fallback}</>;

  return <HtmlRenderer content={html} />;
}

const VIEW_OPTIONS: { value: PreviewView; label: string; title: string }[] = [
  {
    value: "parsed",
    label: "Parsed",
    title: "Paperclip's parsed view (text, math, figures)",
  },
  {
    value: "raw",
    label: "Raw",
    title: "The original file you uploaded",
  },
];

function ViewToggle({
  view,
  onViewChange,
}: {
  view: PreviewView;
  onViewChange: (v: PreviewView) => void;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const selectAt = (index: number) => {
    const next = VIEW_OPTIONS[index];
    if (!next) return;
    onViewChange(next.value);
    refs.current[index]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = VIEW_OPTIONS.findIndex((o) => o.value === view);
    if (current < 0) return;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      selectAt((current + 1) % VIEW_OPTIONS.length);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      selectAt((current - 1 + VIEW_OPTIONS.length) % VIEW_OPTIONS.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      selectAt(0);
    } else if (e.key === "End") {
      e.preventDefault();
      selectAt(VIEW_OPTIONS.length - 1);
    }
  };

  const base = "rounded px-2 py-0.5 text-xs font-medium transition-colors";
  return (
    <div
      role="radiogroup"
      aria-label="Preview view"
      onKeyDown={onKeyDown}
      className="mr-1 inline-flex items-center gap-0.5 rounded-[3px] border border-zinc-200 bg-zinc-50 p-0.5"
    >
      {VIEW_OPTIONS.map((option, index) => {
        const checked = view === option.value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            title={option.title}
            onClick={() => onViewChange(option.value)}
            className={cn(
              base,
              checked
                ? "bg-white text-zinc-900 shadow-sm"
                : "text-zinc-500 hover:text-zinc-900",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function PreviewHeader({
  preview,
  onClose,
  onExpand,
  expandButtonRef,
  view,
  onViewChange,
  canShowRaw,
}: {
  preview: DocumentContent | null;
  onClose: () => void;
  onExpand?: () => void;
  expandButtonRef?: RefObject<HTMLButtonElement | null>;
  view?: PreviewView;
  onViewChange?: (v: PreviewView) => void;
  canShowRaw?: boolean;
}) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-zinc-100 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate font-mono text-sm font-semibold text-zinc-900">
          {preview
            ? preview.virtual_file?.name ??
              `usr_${String(preview.document.id).replace(/-/g, "").slice(0, 12)}`
            : "Loading\u2026"}
        </p>
        {preview && (
          <p className="truncate text-xs text-zinc-500">
            {preview.virtual_file
              ? `${String(preview.document.filename)} / ${preview.virtual_file.path}`
              : `${String(preview.document.filename)} · ${preview.total_lines} lines`}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {canShowRaw && view && onViewChange && (
          <ViewToggle view={view} onViewChange={onViewChange} />
        )}
        {onExpand && (
          <Button
            ref={expandButtonRef}
            variant="ghost"
            size="icon"
            onClick={onExpand}
            aria-label="Expand preview"
          >
            <Maximize2 className="h-4 w-4" aria-hidden />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close preview"
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

export const ClipboardPreviewPanel = ({ onClose }: { onClose: () => void }) => {
  const { clipboardPreview: preview, clipboardPreviewLoading: loading } =
    useTreeViewOutletContext();
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState<PreviewView>("parsed");
  // Keep a stable trigger node for Radix focus restore (header also mounts inside the dialog).
  const expandButtonRef = useRef<HTMLButtonElement>(null);

  const fileType = useMemo(
    () =>
      detectFileType(
        preview?.virtual_file?.name ?? preview?.document.filename ?? "",
      ),
    [preview?.document.filename, preview?.virtual_file?.name],
  );

  // Raw view only applies to uploaded files — corpus links and folders have no
  // original file. Default back to "parsed" whenever the previewed doc changes.
  const canShowRaw = useMemo(() => {
    const doc = preview?.document;
    if (!doc) return false;
    if (preview.virtual_file) return false;
    const st = doc.source_type;
    return doc.link_type !== "corpus_link" && st !== "link" && st !== "folder";
  }, [preview]);

  useEffect(() => {
    // An explicit request (e.g. "Open original") wins; otherwise images show
    // the original immediately (they have no parsed text representation) and
    // everything else opens in the parsed renderer.
    setView(
      preview?.initial_view ??
        (!preview?.virtual_file && isBrowserImage(preview?.document.filename ?? "")
          ? "raw"
          : "parsed"),
    );
  }, [preview]);

  const ownerId = typeof preview?.document?.user_id === "string" ? preview.document.user_id : undefined;

  const handleExpand = useCallback(() => setModalOpen(true), []);

  const previewTitle = preview
    ? preview.virtual_file?.name ??
      `usr_${String(preview.document.id).replace(/-/g, "").slice(0, 12)}`
    : "Document preview";

  if (!preview && !loading) return null;

  return (
    <>
      <div className="flex h-full w-full flex-col overflow-hidden bg-white">
        <PreviewHeader
          preview={preview}
          onClose={onClose}
          onExpand={handleExpand}
          expandButtonRef={expandButtonRef}
          view={view}
          onViewChange={setView}
          canShowRaw={canShowRaw}
        />
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-12">
          <PreviewContent
            preview={preview}
            loading={loading}
            fileType={fileType}
            ownerId={ownerId}
            view={view}
          />
        </div>
      </div>

      <Dialog
        open={modalOpen}
        onOpenChange={setModalOpen}
      >
        <DialogContent
          hideCloseButton
          className="flex h-[85vh] w-[90vw] max-w-5xl flex-col gap-0 overflow-hidden p-0"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            expandButtonRef.current?.focus();
          }}
        >
          <DialogTitle className="sr-only">{previewTitle}</DialogTitle>
          <DialogDescription className="sr-only">
            Expanded document preview
          </DialogDescription>
          <PreviewHeader
            preview={preview}
            onClose={() => setModalOpen(false)}
            view={view}
            onViewChange={setView}
            canShowRaw={canShowRaw}
          />
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <PreviewContent
              preview={preview}
              loading={loading}
              fileType={fileType}
              ownerId={ownerId}
              view={view}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
